<template>
    <section class="chooser">
        <h2 v-if="title"
            class="chooser__title">
            {{ title }}
        </h2>
        <div class="chooser__options">
            <choice v-for="option in options"
                    :key="option.value"
                    :text="option.text"
                    :value="option.value"
                    :image-url="option.imageUrl"
                    :active="isActive(option)"
                    @click="onClick" />
            <dynamic-choice v-if="allowDynamic"
                            :text="dynamicDefaultText"
                            v-model="customValue"
                            :active="isDynamicActive()"
                            @click="onDynamicClick" />
            <div v-if="useSpacer"
                 class="chooser__options__spacer" />
        </div>
        <div v-if="message"
             class="chooser__message">
            {{ message }}
        </div>
    </section>
</template>

<script>

import Choice from './choice'
import DynamicChoice from './dynamic-choice'

export default {

    props: {
        options: {
            type: Array,
            required: true,
        },
        allowDynamic: {
            type: Boolean,
            default: false,
        },
        dynamicDefaultText: {
            type: String,
            default: '(enter value)',
        },
        dynamicDefaultValue: {
            type: [ String, Number ],
            default: '',
        },
        title: {
            type: String,
            default: null,
        },
        value: {
            type: [ String, Number ],
            required: true,
        },
    },

    data() {
        return {
            innerValue: this.value,
            selectDynamicOverride: false,
        }
    },

    computed: {

        customValue: {
            get() {
                return this.selectedOption ? this.dynamicDefaultValue : this.innerValue ? this.innerValue : this.dynamicDefaultValue
            },
            set(value) {
                this.innerValue = value
                this.$emit('input', value)
            },
        },

        message() {
            return this.selectedOption && this.selectedOption.selectedMessage ? this.selectedOption.selectedMessage : null
        },

        selectedOption() {
            return this.selectDynamicOverride
                ? null
                : this.options.find(op => op.value === this.innerValue)
        },

        useSpacer() {
            const noOfItems = this.options.length + this.allowDynamic ? 1 : 0
            return noOfItems % 2 === 1
        },
    },

    watch: {

        value(newValue) {
            this.innerValue = newValue
        },
    },

    methods: {

        isActive(option) {
            return !this.selectDynamicOverride && Boolean(this.innerValue) && option.value === this.innerValue
        },

        isDynamicActive() {
            return this.selectDynamicOverride || this.isActive({ value: this.customValue })
        },

        onClick(value) {
            this.innerValue = value
            this.selectDynamicOverride = false
            this.$emit('input', value)

            if (this.message)
                this.$emit('click', { value, message: this.message })
            else
                this.$emit('click', { value })
        },

        onDynamicClick(value) {
            this.onClick(value)
            this.selectDynamicOverride = true
        },
    },

    components: {
        Choice,
        DynamicChoice,
    },
}

</script>

<style lang="scss">

// Ensure colours and other visual style elements are added to theme files, not placed in here.
// src\scss\schemes\components\plan-chooser\_chooser.scss

.chooser {
    text-align: left;

    @include element('title') {
        font-size: 1.1rem;
        text-align: center;
        margin-bottom: 0.3rem;

        @include bp-min-mid {
            font-size: 1.3rem;
            text-align: left;
        }

        @include bp-min-wide {
            font-size: 1.4rem;
        }
    }

    @include element('options') {
        @include unstyle-list;
        display: flex;
        flex-direction: column;
        justify-content: space-around;
        align-items: stretch;
        align-content: stretch;
        padding: 0;

        > * {
            flex: 1 1 100%;
            margin: 5px 0;
            padding: 0;
        }

        @include bp-min-small {
            max-width: 520px;
            margin: 0 auto;
            flex-direction: row;
            flex-wrap: wrap;

            > * {
                flex: 1 1 45%;
                margin: 5px;
            }
        }

        @include bp-min-mid {
            max-width: none;
            width: 680px;
            padding: 0 10px;

            > * {
                flex: 1 1 23%;
                margin: 0 1%;
            }
        }

        @include bp-min-wide {
            width: 950px;
        }

        @include element("spacer") {
            display: none;
            height: 0 !important;
            margin-bottom: 0 !important;

            @include bp-min-small {
                display: block;
            }

            @include bp-min-mid {
                display: none;
            }
        }
    }

    @include element('message') {
        width: 100%;
        text-align: center;
        margin: 0.3rem auto;

        @include bp-min-small {
            max-width: 480px;
        }

        @include bp-min-mid {
            max-width: 580px;
            margin: 0.5rem auto;
        }
    }
}

</style>
